import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Users, FileText, AlertCircle, CheckCircle2, Clock, Mail, Calendar as CalendarIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { DocumentWatermark } from "@/components/DocumentWatermark";
import { getDocumentVersion, initializeDocument } from "@/lib/documentVersion";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface BetroffenenRecht {
  artikel: string;
  titel: string;
  beschreibung: string;
  frist: string;
  prozess: string[];
  vorlage: string;
  prioritaet: "high" | "medium" | "low";
}

interface ErledigtStatus {
  [artikel: string]: {
    erledigt: boolean;
    datum?: Date;
  };
}

const AdminBetroffenenrechte = () => {
  const { toast } = useToast();
  const [docVersion, setDocVersion] = useState(getDocumentVersion('betroffenenrechte'));
  const [erledigtStatus, setErledigtStatus] = useState<ErledigtStatus>(() => {
    const saved = localStorage.getItem('betroffenenrechte-erledigt');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    const version = initializeDocument('betroffenenrechte', '1.0');
    setDocVersion(version);
  }, []);

  useEffect(() => {
    localStorage.setItem('betroffenenrechte-erledigt', JSON.stringify(erledigtStatus));
  }, [erledigtStatus]);

  const toggleErledigt = (artikel: string) => {
    setErledigtStatus(prev => ({
      ...prev,
      [artikel]: {
        erledigt: !prev[artikel]?.erledigt,
        datum: !prev[artikel]?.erledigt ? new Date() : undefined
      }
    }));
  };

  const setErledigtDatum = (artikel: string, datum: Date | undefined) => {
    setErledigtStatus(prev => ({
      ...prev,
      [artikel]: {
        erledigt: prev[artikel]?.erledigt || false,
        datum
      }
    }));
  };

  const betroffenenrechte: BetroffenenRecht[] = [
    {
      artikel: "Art. 15 DSGVO",
      titel: "Auskunftsrecht",
      beschreibung: "Betroffene haben das Recht, Auskunft über ihre gespeicherten personenbezogenen Daten zu erhalten.",
      frist: "1 Monat (unverzüglich)",
      prioritaet: "high",
      prozess: [
        "Identität der anfragenden Person prüfen",
        "Alle gespeicherten Daten zusammenstellen",
        "Verarbeitungszwecke dokumentieren",
        "Empfänger und Speicherdauer angeben",
        "Über Rechte (Berichtigung, Löschung, etc.) informieren",
        "Antwort in verständlicher Form übermitteln"
      ],
      vorlage: `Betreff: Ihre Auskunftsanfrage nach Art. 15 DSGVO

Sehr geehrte/r Frau/Herr Dr. Mustermann,

vielen Dank für Ihre Anfrage vom [Datum] nach Art. 15 DSGVO. Gerne erteilen wir Ihnen Auskunft über die bei uns gespeicherten personenbezogenen Daten.

**1. GESPEICHERTE PERSONENBEZOGENE DATEN:**

Kontaktdaten:
- Name: Dr. Max Mustermann
- E-Mail-Adresse: max.mustermann@zahnarztpraxis-beispiel.de
- Telefonnummer: +43 123 456789
- Adresse der Praxis: Musterstraße 123, 1010 Wien

Kommunikationsdaten:
- Kontaktanfragen über Website: 3 Anfragen (letzte vom 15.01.2025)
- Terminbuchungen: 1 Videocall-Termin am 22.01.2025
- Newsletter-Abonnement: Nein

**2. ZWECKE DER VERARBEITUNG:**
- Bearbeitung Ihrer Produktanfragen und Kontaktaufnahmen
- Terminvereinbarung und -verwaltung für Produktberatung
- Versand von Produktinformationen und technischen Unterlagen
- Kundenkommunikation im Rahmen der Geschäftsbeziehung

**3. RECHTSGRUNDLAGE:**
- Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung und -durchführung)
- Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an Kundenpflege)

**4. EMPFÄNGER DER DATEN:**
Ihre Daten wurden an folgende Empfänger weitergegeben:
- Lovable Cloud (Supabase) - Hosting und Datenbankdienste (EU-Server Frankfurt)
- World4You - E-Mail-Versand (Österreich)
- Keine Weitergabe an weitere Dritte

**5. SPEICHERDAUER:**
- Kontakt- und Kommunikationsdaten: 3 Jahre nach letztem Kontakt
- Vertragsdaten: 7 Jahre gemäß § 132 BAO (bei Vertragsabschluss)
- Terminbuchungen: 1 Jahr nach Terminende

**6. HERKUNFT DER DATEN:**
- Direkt von Ihnen über unser Kontaktformular auf eap-abutments.com
- Direkt von Ihnen via E-Mail-Korrespondenz

**7. KEINE AUTOMATISIERTE ENTSCHEIDUNGSFINDUNG:**
Es findet keine automatisierte Entscheidungsfindung oder Profiling statt.

**8. IHRE RECHTE:**
Sie haben gemäß DSGVO folgende Rechte:
- Recht auf Berichtigung (Art. 16 DSGVO)
- Recht auf Löschung (Art. 17 DSGVO)
- Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Recht auf Datenübertragbarkeit (Art. 20 DSGVO)
- Widerspruchsrecht (Art. 21 DSGVO)
- Recht auf Beschwerde bei der Datenschutzbehörde

Kontakt Datenschutzbehörde Österreich:
Österreichische Datenschutzbehörde
Wickenburggasse 8, 1080 Wien
Telefon: +43 1 521 52-25 69
E-Mail: dsb@dsb.gv.at

Sollten Sie weitere Fragen haben, stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.
Geschäftsführer

EAP® Produktions- und Patentverwertungs-GmbH
Lorettostraße 26 | 6060 Hall in Tirol
E-Mail: datenschutz@eap-abutments.com
Tel: +43 (0)677 620 565 43
Web: eap-abutments.com`
    },
    {
      artikel: "Art. 16 DSGVO",
      titel: "Recht auf Berichtigung",
      beschreibung: "Betroffene können die Berichtigung unrichtiger oder die Vervollständigung unvollständiger Daten verlangen.",
      frist: "Unverzüglich",
      prioritaet: "high",
      prozess: [
        "Anfrage prüfen und Person identifizieren",
        "Betroffene Datensätze lokalisieren",
        "Korrektur der Daten durchführen",
        "Empfänger der Daten über Berichtigung informieren",
        "Betroffene Person über Berichtigung informieren",
        "Vorgang dokumentieren"
      ],
      vorlage: `Betreff: Berichtigung Ihrer Daten nach Art. 16 DSGVO

Sehr geehrte/r Frau/Herr Dr. Mustermann,

vielen Dank für Ihre Anfrage vom [Datum] nach Art. 16 DSGVO zur Berichtigung Ihrer personenbezogenen Daten.

Wir haben Ihre Daten wie gewünscht unverzüglich korrigiert:

**DURCHGEFÜHRTE KORREKTUREN:**

1. E-Mail-Adresse:
   Alt: max.mustermann@alte-praxis.de
   Neu: max.mustermann@neue-praxis.de

2. Praxisadresse:
   Alt: Musterstraße 123, 1010 Wien
   Neu: Hauptplatz 5, 1020 Wien

3. Telefonnummer:
   Alt: +43 123 456789
   Neu: +43 987 654321

**STATUS:**
✓ Die Änderungen wurden am [Datum, Uhrzeit] in unserem System durchgeführt
✓ Alle betroffenen Datensätze wurden aktualisiert
✓ Die korrigierten Daten sind ab sofort wirksam

**EMPFÄNGER INFORMIERT:**
Folgende Empfänger, an die Ihre Daten weitergegeben wurden, wurden über die Berichtigung informiert:
- Lovable Cloud (Supabase) - Hosting-Provider
- World4You - E-Mail-Provider

**DOKUMENTATION:**
Ihre Berichtigungsanfrage und deren Durchführung wurden in unserem Datenschutz-Protokoll dokumentiert.

Sollten weitere Daten unrichtig oder unvollständig sein, teilen Sie uns dies bitte mit. Wir werden die entsprechenden Korrekturen umgehend vornehmen.

Mit freundlichen Grüßen
Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.

EAP® Produktions- und Patentverwertungs-GmbH
Lorettostraße 26 | 6060 Hall in Tirol
E-Mail: datenschutz@eap-abutments.com
Tel: +43 (0)677 620 565 43`
    },
    {
      artikel: "Art. 17 DSGVO",
      titel: "Recht auf Löschung ('Recht auf Vergessenwerden')",
      beschreibung: "Betroffene können die Löschung ihrer Daten verlangen, wenn kein Grund für die weitere Verarbeitung besteht.",
      frist: "Unverzüglich",
      prioritaet: "high",
      prozess: [
        "Löschanfrage prüfen",
        "Gesetzliche Aufbewahrungsfristen prüfen",
        "Bei berechtigtem Interesse: Daten löschen",
        "Empfänger der Daten über Löschung informieren",
        "Löschung bestätigen",
        "Bei Ablehnung: Gründe mitteilen (z.B. gesetzliche Aufbewahrungspflicht)"
      ],
      vorlage: `Betreff: Löschung Ihrer Daten nach Art. 17 DSGVO - [VOLLSTÄNDIG DURCHGEFÜHRT / TEILWEISE EINGESCHRÄNKT]

Sehr geehrte/r Frau/Herr Dr. Mustermann,

vielen Dank für Ihre Anfrage vom [Datum] nach Art. 17 DSGVO zur Löschung Ihrer personenbezogenen Daten.

**OPTION A - VOLLSTÄNDIGE LÖSCHUNG:**

Wir haben Ihrer Löschanfrage vollständig entsprochen und alle Ihre personenbezogenen Daten unverzüglich gelöscht.

**Gelöschte Daten:**
- Kontaktdaten (Name, E-Mail, Telefonnummer, Adresse)
- Alle E-Mail-Korrespondenzen und Kontaktanfragen
- Terminbuchungen und Beratungsnotizen
- Account-Informationen (sofern vorhanden)

**Gelöscht aus folgenden Systemen:**
- Hauptdatenbank (Lovable Cloud/Supabase)
- E-Mail-System (World4You)
- Backup-Systeme (werden innerhalb von 30 Tagen überschrieben)

**Empfänger informiert:**
- Lovable Cloud (Supabase) - Hosting
- World4You - E-Mail-Services

Die Löschung wurde am [Datum, Uhrzeit] durchgeführt und dokumentiert.

---

**OPTION B - TEILWEISE EINSCHRÄNKTE LÖSCHUNG:**

Wir haben Ihre Löschanfrage geprüft. Leider können wir Ihrer Anfrage derzeit nicht vollständig nachkommen, da folgende gesetzliche Aufbewahrungspflichten bestehen:

**Nicht löschbare Daten (mit Begründung):**

1. Rechnungsdaten (bei erfolgtem Vertragsabschluss):
   - Aufbewahrungspflicht: 7 Jahre gemäß § 132 BAO (Bundesabgabenordnung)
   - Betroffene Daten: Rechnungsnummer, Rechnungsdatum, Leistungsbeschreibung, Betrag
   - Automatische Löschung: [Datum in 7 Jahren]

2. Vertragsdokumente:
   - Aufbewahrungspflicht: Gewährleistungsfristen (2-3 Jahre ab Lieferung)
   - Betroffene Daten: Vertragsunterlagen, Lieferbestätigungen
   - Automatische Löschung: [Datum nach Gewährleistungsablauf]

**Bereits gelöschte Daten:**
- Kontaktformular-Anfragen (nicht buchhalterisch relevant)
- Marketing-Einwilligungen
- Website-Aktivitätsdaten

**Ihre Rechte:**
Nach Ablauf der gesetzlichen Aufbewahrungsfristen werden alle Daten automatisch gelöscht. Sie haben weiterhin das Recht auf:
- Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)
- Beschwerde bei der Datenschutzbehörde

Bei Fragen zur Aufbewahrungspflicht stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.

EAP® Produktions- und Patentverwertungs-GmbH
Lorettostraße 26 | 6060 Hall in Tirol
E-Mail: datenschutz@eap-abutments.com
Tel: +43 (0)677 620 565 43`
    },
    {
      artikel: "Art. 18 DSGVO",
      titel: "Recht auf Einschränkung der Verarbeitung",
      beschreibung: "Betroffene können verlangen, dass die Verarbeitung ihrer Daten eingeschränkt wird.",
      frist: "Unverzüglich",
      prioritaet: "medium",
      prozess: [
        "Anfrage prüfen und Grund ermitteln",
        "Betroffene Daten markieren/sperren",
        "Nur eingeschränkte Verarbeitung zulassen",
        "Empfänger über Einschränkung informieren",
        "Betroffene Person vor Aufhebung informieren",
        "Einschränkung dokumentieren"
      ],
      vorlage: `Betreff: Einschränkung der Verarbeitung nach Art. 18 DSGVO

Sehr geehrte/r Frau/Herr Dr. Mustermann,

vielen Dank für Ihre Anfrage vom [Datum] nach Art. 18 DSGVO zur Einschränkung der Verarbeitung Ihrer personenbezogenen Daten.

Wir haben Ihrer Anfrage entsprochen und die Verarbeitung Ihrer Daten wie folgt eingeschränkt:

**DURCHGEFÜHRTE MASSNAHMEN:**

1. Sperrung der Daten:
   - Ihre Daten wurden in unserem System als "Verarbeitung eingeschränkt" markiert
   - Die Daten sind weiterhin gespeichert, werden aber nicht mehr aktiv verarbeitet
   - Keine automatische Verwendung für Geschäftsprozesse

2. Eingeschränkte Verarbeitungszwecke:
   ✓ Speicherung der Daten (ohne aktive Verarbeitung)
   ✓ Verarbeitung nur mit Ihrer ausdrücklichen Einwilligung
   ✓ Verarbeitung zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen
   ✓ Verarbeitung zum Schutz der Rechte anderer Personen

3. Nicht mehr durchgeführt:
   ✗ Versand von Produktinformationen
   ✗ Kontaktaufnahme für Marketingzwecke
   ✗ Verwendung für statistische Auswertungen
   ✗ Weitergabe an Dritte (außer gesetzlich vorgeschrieben)

**EMPFÄNGER INFORMIERT:**
Folgende Empfänger wurden über die Einschränkung informiert:
- Lovable Cloud (Supabase) - technische Sperrung implementiert
- World4You - E-Mail-Kommunikation gestoppt

**GRUND DER EINSCHRÄNKUNG:**
[Hier eintragen: z.B. "Prüfung der Richtigkeit der Daten läuft" / "Widerspruch gegen Verarbeitung eingelegt"]

**DAUER DER EINSCHRÄNKUNG:**
Die Einschränkung bleibt bestehen bis:
- Die Richtigkeit der Daten geklärt ist, oder
- Der Zweck der Verarbeitung entfallen ist, oder
- Sie die Aufhebung der Einschränkung verlangen

**WICHTIGER HINWEIS:**
Vor Aufhebung der Einschränkung werden wir Sie rechtzeitig informieren. Sie können die Einschränkung jederzeit aufheben lassen, indem Sie uns kontaktieren.

**DOKUMENTATION:**
Die Einschränkung wurde am [Datum, Uhrzeit] in unserem Datenschutz-Protokoll dokumentiert.

Bei Fragen stehen wir Ihnen jederzeit zur Verfügung.

Mit freundlichen Grüßen
Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.

EAP® Produktions- und Patentverwertungs-GmbH
Lorettostraße 26 | 6060 Hall in Tirol
E-Mail: datenschutz@eap-abutments.com
Tel: +43 (0)677 620 565 43`
    },
    {
      artikel: "Art. 20 DSGVO",
      titel: "Recht auf Datenübertragbarkeit",
      beschreibung: "Betroffene können verlangen, ihre Daten in einem strukturierten, gängigen Format zu erhalten.",
      frist: "1 Monat",
      prioritaet: "medium",
      prozess: [
        "Anfrage prüfen",
        "Alle relevanten Daten exportieren",
        "In maschinenlesbarem Format bereitstellen (z.B. CSV, JSON)",
        "Sichere Übermittlung vorbereiten",
        "Daten übermitteln oder zum Download bereitstellen",
        "Übermittlung bestätigen"
      ],
      vorlage: `Betreff: Datenübertragbarkeit nach Art. 20 DSGVO - Ihre Daten im exportierbaren Format

Sehr geehrte/r Frau/Herr Dr. Mustermann,

vielen Dank für Ihre Anfrage vom [Datum] nach Art. 20 DSGVO zur Datenübertragbarkeit.

Wir haben alle Ihre personenbezogenen Daten, die Sie uns bereitgestellt haben, in einem strukturierten, maschinenlesbaren Format exportiert.

**EXPORTIERTE DATEN:**

Die beigefügten Dateien enthalten folgende Daten:

1. **Datei: EAP_Kontaktdaten_[Name].csv**
   - Persönliche Kontaktdaten
   - Praxisinformationen
   - Kommunikationspräferenzen

2. **Datei: EAP_Kommunikationshistorie_[Name].json**
   - Alle Kontaktanfragen und E-Mails
   - Terminbuchungen und deren Status
   - Beratungsgespräche-Notizen

3. **Datei: EAP_Vertragsdetails_[Name].csv** (falls vorhanden)
   - Bestellhistorie
   - Rechnungsinformationen
   - Lieferdetails

**FORMAT-INFORMATIONEN:**
- CSV-Dateien: Kompatibel mit Excel, Google Sheets, etc.
- JSON-Dateien: Maschinenlesbar, für technische Weiterverarbeitung
- Zeichensatz: UTF-8
- Datums-Format: ISO 8601 (YYYY-MM-DD)

**RECHTSGRUNDLAGE:**
Die exportierten Daten basieren auf:
- Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung/-durchführung)
- Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)

**SICHERE ÜBERMITTLUNG:**
Die Dateien sind passwortgeschützt.
Passwort: [Wird separat per SMS/Telefon übermittelt]

**DIREKTÜBERTRAGUNG:**
Falls Sie möchten, dass wir Ihre Daten direkt an einen anderen Verantwortlichen übermitteln, teilen Sie uns bitte folgende Informationen mit:
- Name und Kontaktdaten des Empfängers
- Technische Schnittstelle (falls vorhanden)

**NICHT ENTHALTEN:**
Folgende Daten sind nicht im Export enthalten, da sie nicht auf Grundlage einer Einwilligung oder eines Vertrags verarbeitet werden:
- Intern generierte Metadaten (z.B. Log-Dateien)
- Daten mit gesetzlicher Aufbewahrungspflicht ohne Ihre Einwilligung

Bei Fragen zur Verwendung der exportierten Daten stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.

EAP® Produktions- und Patentverwertungs-GmbH
Lorettostraße 26 | 6060 Hall in Tirol
E-Mail: datenschutz@eap-abutments.com
Tel: +43 (0)677 620 565 43`
    },
    {
      artikel: "Art. 21 DSGVO",
      titel: "Widerspruchsrecht",
      beschreibung: "Betroffene können der Verarbeitung ihrer Daten aus Gründen ihrer besonderen Situation widersprechen.",
      frist: "Unverzüglich",
      prioritaet: "high",
      prozess: [
        "Widerspruch prüfen",
        "Verarbeitungsgrundlage ermitteln",
        "Bei berechtigtem Widerspruch: Verarbeitung einstellen",
        "Bei Direktwerbung: Sofort einstellen",
        "Bei überwiegendem Interesse: Begründung mitteilen",
        "Betroffene Person informieren"
      ],
      vorlage: `Betreff: Widerspruch gegen Datenverarbeitung nach Art. 21 DSGVO

Sehr geehrte/r Frau/Herr Dr. Mustermann,

vielen Dank für Ihren Widerspruch vom [Datum] nach Art. 21 DSGVO gegen die Verarbeitung Ihrer personenbezogenen Daten.

**OPTION A - WIDERSPRUCH AKZEPTIERT:**

Wir haben Ihrem Widerspruch entsprochen und die Verarbeitung Ihrer Daten eingestellt.

**Eingestellte Verarbeitungen:**
- Versand von Produktinformationen und Newslettern
- Kontaktaufnahme für Marketingzwecke
- Verwendung für statistische Auswertungen
- Profiling und Segmentierung

**Weiterhin gespeicherte Daten:**
Falls gesetzliche Aufbewahrungspflichten bestehen (z.B. § 132 BAO), werden Ihre Daten ausschließlich zu folgenden Zwecken gespeichert:
- Erfüllung rechtlicher Verpflichtungen
- Geltendmachung von Rechtsansprüchen

**Durchgeführt am:** [Datum, Uhrzeit]

**Empfänger informiert:**
- Lovable Cloud (Supabase)
- World4You
- Alle weiteren Auftragsverarbeiter

---

**OPTION B - WIDERSPRUCH GEGEN DIREKTWERBUNG:**

Wir haben Ihrem Widerspruch gegen Direktwerbung nach Art. 21 Abs. 2 DSGVO entsprochen.

**Sofort eingestellt:**
✓ Versand von Marketing-E-Mails
✓ Produktinformationen und Angebote
✓ Newsletter und Werbe-Kommunikation
✓ Telefonische Kontaktaufnahme zu Werbezwecken

Ihre Daten werden ab sofort nicht mehr für Direktwerbung verwendet.

**Weiterhin möglich:**
- Kommunikation im Rahmen bestehender Verträge
- Beantwortung von Anfragen, die Sie an uns richten
- Gesetzlich vorgeschriebene Informationen

---

**OPTION C - WIDERSPRUCH KANN NICHT ENTSPROCHEN WERDEN:**

Wir haben Ihren Widerspruch sorgfältig geprüft. Leider können wir diesem nicht vollständig entsprechen, da zwingende schutzwürdige Gründe für die Verarbeitung vorliegen:

**Gründe für die Fortsetzung der Verarbeitung:**

1. Vertragserfüllung:
   - Laufende Verträge erfordern die weitere Verarbeitung Ihrer Daten
   - Gewährleistungsansprüche und Produkthaftung
   - Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO

2. Gesetzliche Verpflichtungen:
   - Aufbewahrungspflichten nach § 132 BAO (7 Jahre)
   - Produkthaftungsvorschriften
   - Rechtsgrundlage: Art. 6 Abs. 1 lit. c DSGVO

3. Geltendmachung von Rechtsansprüchen:
   - Mögliche Gewährleistungs- oder Schadensersatzansprüche
   - Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO

**Ihre Rechte:**
Trotz Ablehnung des Widerspruchs haben Sie weiterhin das Recht auf:
- Einschränkung der Verarbeitung (Art. 18 DSGVO)
- Beschwerde bei der Datenschutzbehörde

**Kontakt Datenschutzbehörde:**
Österreichische Datenschutzbehörde
Wickenburggasse 8, 1080 Wien
Tel: +43 1 521 52-25 69
E-Mail: dsb@dsb.gv.at

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.

EAP® Produktions- und Patentverwertungs-GmbH
Lorettostraße 26 | 6060 Hall in Tirol
E-Mail: datenschutz@eap-abutments.com
Tel: +43 (0)677 620 565 43`
    },
    {
      artikel: "Art. 22 DSGVO",
      titel: "Schutz vor automatisierten Entscheidungen",
      beschreibung: "Betroffene haben das Recht, nicht einer ausschließlich automatisierten Entscheidung unterworfen zu werden.",
      frist: "Unverzüglich",
      prioritaet: "low",
      prozess: [
        "Prüfen, ob automatisierte Entscheidung vorliegt",
        "Falls ja: Menschliche Intervention ermöglichen",
        "Transparenz über Entscheidungslogik herstellen",
        "Möglichkeit zur Stellungnahme bieten",
        "Entscheidung überprüfen lassen",
        "Betroffene Person informieren"
      ],
      vorlage: `Betreff: Automatisierte Entscheidungsfindung nach Art. 22 DSGVO

Sehr geehrte/r Frau/Herr Dr. Mustermann,

vielen Dank für Ihre Anfrage vom [Datum] nach Art. 22 DSGVO bezüglich automatisierter Entscheidungsfindung.

**UNSERE PRAXIS BEI EAP:**

Wir nehmen Ihre Anfrage sehr ernst und möchten Ihnen transparent über unsere Verarbeitungsprozesse Auskunft geben.

**1. KEINE VOLLAUTOMATISIERTEN ENTSCHEIDUNGEN:**

Bei EAP® Produktions- und Patentverwertungs-GmbH werden KEINE ausschließlich automatisierten Entscheidungen getroffen, die Ihnen gegenüber rechtliche Wirkung entfalten oder Sie in ähnlicher Weise erheblich beeinträchtigen.

**Alle wesentlichen Entscheidungen erfolgen durch Menschen:**
✓ Angebotserstellung: Manuell durch Vertriebsmitarbeiter
✓ Vertragsabschlüsse: Persönliche Prüfung und Freigabe
✓ Kundenberatung: Individuelle Betreuung durch Fachpersonal
✓ Beschwerdemanagement: Persönliche Bearbeitung

**2. VERWENDETE AUTOMATISIERTE PROZESSE:**

Folgende automatisierte Prozesse werden eingesetzt (OHNE rechtliche Wirkung):

a) **Website-Analytics (Google Analytics 4):**
   - Zweck: Verbesserung der Website-Nutzererfahrung
   - Keine individuellen Entscheidungen über Personen
   - Nur anonymisierte, statistische Auswertungen
   - Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)

b) **E-Mail-System (Automatische Antworten):**
   - Zweck: Automatische Eingangsbestätigung von Kontaktanfragen
   - Keine Entscheidungen, nur Bestätigungsnachrichten
   - Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)

c) **Terminbuchungssystem:**
   - Zweck: Verfügbarkeitsprüfung und Terminreservierung
   - Keine bindenden Entscheidungen ohne menschliche Bestätigung
   - Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung)

**3. KEIN PROFILING MIT RECHTLICHER WIRKUNG:**

Wir führen KEIN Profiling durch, das:
- Ihre Kreditwürdigkeit bewertet
- Vertragsabschlüsse automatisch ablehnt
- Preise automatisch individuell festlegt
- Ihren Zugang zu Leistungen automatisch einschränkt

**4. IHRE RECHTE:**

Sollten wir in Zukunft automatisierte Entscheidungsfindung einführen:
✓ Sie werden vorab informiert
✓ Sie können eine menschliche Überprüfung verlangen
✓ Sie können Ihren Standpunkt darlegen
✓ Sie können die Entscheidung anfechten

**5. TRANSPARENZ:**

Bei Fragen zu unseren Verarbeitungsprozessen können Sie jederzeit:
- Eine detaillierte Auskunft über die verwendete Logik verlangen
- Eine menschliche Überprüfung bestimmter Prozesse anfordern
- Widerspruch gegen bestimmte Verarbeitungen einlegen

**FAZIT:**

Bei EAP® steht der persönliche Kontakt im Mittelpunkt. Alle wichtigen Entscheidungen werden von unserem erfahrenen Team getroffen und nicht von Algorithmen.

Falls Sie weitere Fragen haben oder eine Überprüfung bestimmter Prozesse wünschen, stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
Ing. Dr. med. dent. Mario Kern M.Sc. M.Sc.

EAP® Produktions- und Patentverwertungs-GmbH
Lorettostraße 26 | 6060 Hall in Tirol
E-Mail: datenschutz@eap-abutments.com
Tel: +43 (0)677 620 565 43
Web: eap-abutments.com`
    }
  ];

  const downloadVorlage = (recht: BetroffenenRecht) => {
    const blob = new Blob([recht.vorlage], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Vorlage_${recht.artikel.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Vorlage heruntergeladen",
      description: `${recht.titel} Vorlage wurde gespeichert.`,
    });
  };

  const downloadAlleProzesse = () => {
    const content = betroffenenrechte.map(recht => `
# ${recht.titel} (${recht.artikel})

**Frist:** ${recht.frist}

## Beschreibung
${recht.beschreibung}

## Prozess
${recht.prozess.map((schritt, i) => `${i + 1}. ${schritt}`).join('\n')}

## E-Mail-Vorlage
${recht.vorlage}

---
`).join('\n\n');

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Betroffenenrechte_Prozesse_Komplett.md';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Alle Prozesse heruntergeladen",
      description: "Komplette Dokumentation wurde gespeichert.",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <AdminNavigation />
      <main className="max-w-full overflow-x-hidden pt-24 pb-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <Breadcrumbs items={[
            { label: "Admin" },
            { label: "Dokumente", href: "/admin/documents" },
            { label: "Betroffenenrechte" }
          ]} />
          
          <DocumentWatermark 
            documentName="Betroffenenrechte nach DSGVO"
            version={docVersion}
          />

          <div className="mb-8 flex flex-col items-start justify-between gap-4 mt-6 sm:flex-row">
            <div className="min-w-0">
              <h1 className="break-words text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                Betroffenenrechte
              </h1>
              <p className="text-xl text-muted-foreground">
                DSGVO Art. 15-22 – Prozesse & Vorlagen
              </p>
            </div>
            <Button onClick={downloadAlleProzesse} className="h-auto max-w-full whitespace-normal flex items-center gap-2">
              <Download className="w-4 h-4" />
              Alle Prozesse herunterladen
            </Button>
          </div>

          {/* Wichtige Hinweise */}
          <Alert className="mb-8 border-primary/50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Wichtig:</strong> Anfragen zu Betroffenenrechten müssen innerhalb der gesetzlichen Fristen 
              (meist 1 Monat) beantwortet werden. Bei Nichteinhaltung drohen Bußgelder bis zu 20 Mio. EUR oder 4% des Jahresumsatzes.
              Dokumentieren Sie alle Anfragen und Ihre Antworten sorgfältig.
            </AlertDescription>
          </Alert>

          {/* Kontaktinformationen */}
          <Card className="p-4 sm:p-6 mb-8 bg-muted/30">
            <div className="flex items-start gap-4">
              <Mail className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Kontakt für Betroffenenanfragen
                </h2>
                <p className="text-muted-foreground mb-3">
                  Alle Anfragen zu Betroffenenrechten sollten an folgende Adresse gerichtet werden:
                </p>
                <div className="space-y-1">
                  <p className="break-words text-foreground"><strong>E-Mail:</strong> datenschutz@eap-abutments.com</p>
                  <p className="text-foreground"><strong>Betreff:</strong> "DSGVO - Betroffenenrecht"</p>
                </div>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="uebersicht" className="w-full">
            <TabsList className="flex h-auto w-full justify-start overflow-x-auto mb-8 p-1 [scrollbar-width:thin]">
              <TabsTrigger value="uebersicht" className="shrink-0">Übersicht</TabsTrigger>
              <TabsTrigger value="prozesse" className="shrink-0">Prozesse</TabsTrigger>
              <TabsTrigger value="vorlagen" className="shrink-0">Vorlagen</TabsTrigger>
            </TabsList>

            {/* Übersicht */}
            <TabsContent value="uebersicht">
              <div className="grid md:grid-cols-2 gap-6">
                {betroffenenrechte.map(recht => (
                  <Card key={recht.artikel} className={cn(
                    "p-4 sm:p-6 hover:shadow-lg transition-shadow",
                    erledigtStatus[recht.artikel]?.erledigt && "bg-muted/50 border-primary"
                  )}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <Badge className="mb-2">{recht.artikel}</Badge>
                        <h3 className="text-xl font-semibold text-foreground mb-2">
                          {recht.titel}
                        </h3>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <Badge variant={
                          recht.prioritaet === 'high' ? 'destructive' :
                          recht.prioritaet === 'medium' ? 'default' : 'secondary'
                        }>
                          {recht.prioritaet === 'high' ? 'Hoch' :
                           recht.prioritaet === 'medium' ? 'Mittel' : 'Niedrig'}
                        </Badge>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`erledigt-${recht.artikel}`}
                            checked={erledigtStatus[recht.artikel]?.erledigt || false}
                            onCheckedChange={() => toggleErledigt(recht.artikel)}
                          />
                          <label
                            htmlFor={`erledigt-${recht.artikel}`}
                            className="text-sm font-medium cursor-pointer"
                          >
                            Erledigt
                          </label>
                        </div>
                        {erledigtStatus[recht.artikel]?.erledigt && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "text-xs h-7",
                                  !erledigtStatus[recht.artikel]?.datum && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="w-3 h-3 mr-1" />
                                {erledigtStatus[recht.artikel]?.datum 
                                  ? format(new Date(erledigtStatus[recht.artikel].datum!), "dd.MM.yyyy", { locale: de })
                                  : "Datum"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="end">
                              <Calendar
                                mode="single"
                                selected={erledigtStatus[recht.artikel]?.datum ? new Date(erledigtStatus[recht.artikel].datum!) : undefined}
                                onSelect={(date) => setErledigtDatum(recht.artikel, date)}
                                initialFocus
                                className="pointer-events-auto"
                              />
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-4">
                      {recht.beschreibung}
                    </p>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <Clock className="w-4 h-4" />
                      <span><strong>Frist:</strong> {recht.frist}</span>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm font-medium text-foreground">Wichtigste Schritte:</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {recht.prozess.slice(0, 3).map((schritt, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            {schritt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Prozesse */}
            <TabsContent value="prozesse">
              <div className="space-y-6">
                {betroffenenrechte.map(recht => (
                  <Card key={recht.artikel} className={cn(
                    "p-6",
                    erledigtStatus[recht.artikel]?.erledigt && "bg-muted/50 border-primary"
                  )}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <Badge className="mb-2">{recht.artikel}</Badge>
                        <h3 className="text-2xl font-semibold text-foreground">
                          {recht.titel}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4" />
                          <span className="font-medium">{recht.frist}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`prozess-erledigt-${recht.artikel}`}
                            checked={erledigtStatus[recht.artikel]?.erledigt || false}
                            onCheckedChange={() => toggleErledigt(recht.artikel)}
                          />
                          <label
                            htmlFor={`prozess-erledigt-${recht.artikel}`}
                            className="text-sm font-medium cursor-pointer"
                          >
                            Erledigt
                          </label>
                        </div>
                        {erledigtStatus[recht.artikel]?.erledigt && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "text-xs h-8",
                                  !erledigtStatus[recht.artikel]?.datum && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="w-3 h-3 mr-1" />
                                {erledigtStatus[recht.artikel]?.datum 
                                  ? format(new Date(erledigtStatus[recht.artikel].datum!), "dd.MM.yyyy", { locale: de })
                                  : "Datum"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="end">
                              <Calendar
                                mode="single"
                                selected={erledigtStatus[recht.artikel]?.datum ? new Date(erledigtStatus[recht.artikel].datum!) : undefined}
                                onSelect={(date) => setErledigtDatum(recht.artikel, date)}
                                initialFocus
                                className="pointer-events-auto"
                              />
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-6">
                      {recht.beschreibung}
                    </p>

                    <div className="bg-muted/30 p-4 rounded-lg">
                      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Bearbeitungsprozess
                      </h4>
                      <ol className="space-y-3">
                        {recht.prozess.map((schritt, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
                              {i + 1}
                            </span>
                            <span className="text-foreground pt-0.5">{schritt}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    <div className="mt-4 flex justify-end">
                      <Button 
                        variant="outline" 
                        onClick={() => downloadVorlage(recht)}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Vorlage herunterladen
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Vorlagen */}
            <TabsContent value="vorlagen">
              <div className="space-y-6">
                {betroffenenrechte.map(recht => (
                  <Card key={recht.artikel} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <Badge className="mb-2">{recht.artikel}</Badge>
                        <h3 className="text-xl font-semibold text-foreground">
                          {recht.titel}
                        </h3>
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => downloadVorlage(recht)}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </Button>
                    </div>

                    <div className="bg-muted/30 p-4 rounded-lg font-mono text-sm whitespace-pre-wrap">
                      {recht.vorlage}
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Best Practices */}
          <Card className="p-6 mt-8 border-2 border-primary/20">
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-primary" />
              Best Practices für Betroffenenanfragen
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3 text-sm text-muted-foreground">
                <h3 className="font-semibold text-foreground">Organisatorisch</h3>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Zentrale E-Mail-Adresse einrichten</li>
                  <li>Verantwortlichkeiten klar definieren</li>
                  <li>Alle Anfragen und Antworten dokumentieren</li>
                  <li>Fristen im Kalender eintragen</li>
                  <li>Regelmäßige Schulungen durchführen</li>
                </ul>
              </div>
              <div className="space-y-3 text-sm text-muted-foreground">
                <h3 className="font-semibold text-foreground">Technisch</h3>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Sichere Identitätsprüfung etablieren</li>
                  <li>Datenexport-Funktionen vorbereiten</li>
                  <li>Lösch- und Sperrfunktionen implementieren</li>
                  <li>Verschlüsselte Kommunikation nutzen</li>
                  <li>Ticket-System für Nachverfolgung</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminBetroffenenrechte;
